import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { OpdScreeningData, QueueEntry } from '@his/shared';
import { Activity, Baby, ClipboardList, Utensils } from 'lucide-react';
import { get, post, qs } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { useWorkingBranchId } from '@/lib/branch';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, Checkbox, Field, FormSkeleton, Input } from '@/components/ui/primitives';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from './BookAppointmentPage';

const FALL_ITEMS: [keyof NonNullable<OpdScreeningData['fallRisk']>, string][] = [
  ['historyOfFalls', 'Fell in the last 3 months'],
  ['walkingAid', 'Uses a walking aid'],
  ['dizziness', 'Dizzy or faint'],
  ['unsteadyGait', 'Unsteady walking'],
];

export function ScreeningPage() {
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const [params] = useSearchParams();
  const branchId = useWorkingBranchId();
  const returnTo = safeReturnTo(params.get('returnTo'), '/triage');

  const queue = useQuery({
    queryKey: ['queue', '', 'triage'],
    queryFn: () => get<QueueEntry[]>(`/queue${qs({ doctorId: undefined, allDoctors: true, branchId })}`),
    enabled: Boolean(branchId),
  });
  const entry = queue.data?.find((row) => row.appointmentId === appointmentId) ?? null;

  if (!entry) {
    return (
      <FormScreen title={tr('opd', 'nursing_screening')} icon={ClipboardList} backTo={returnTo}>
        {queue.isLoading ? (
          <FormSkeleton />
        ) : (
          <Alert variant="destructive">{queue.error ? describeError(queue.error) : tr('opd', 'not_in_the_queue')}</Alert>
        )}
      </FormScreen>
    );
  }

  return <ScreeningForm key={entry.appointmentId} entry={entry} returnTo={returnTo} />;
}

function ScreeningForm({ entry, returnTo }: { entry: QueueEntry; returnTo: string }) {
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [pain, setPain] = useState('');
  const [site, setSite] = useState('');
  const [fall, setFall] = useState({ historyOfFalls: false, walkingAid: false, dizziness: false, unsteadyGait: false });
  const [nutrition, setNutrition] = useState({ weightLoss: false, reducedIntake: false });
  const [pregnancy, setPregnancy] = useState('');
  const [error, setError] = useState<string | null>(null);
  const woman = entry.sex === 'female';

  const save = useMutation({
    mutationFn: () => {
      const data: OpdScreeningData = {
        ...(pain !== '' ? { pain: { score: Number(pain), ...(site.trim() ? { site: site.trim() } : {}) } } : {}),
        fallRisk: fall,
        nutrition,
        ...(woman && pregnancy ? { pregnancy: { status: pregnancy as 'not_pregnant' | 'pregnant' | 'possible' | 'unknown' } } : {}),
      };
      return post('/opd-screenings', { appointmentId: entry.appointmentId, data });
    },
    onSuccess: () => {
      toast.success(tr('opd', 'screening_recorded'));
      void queryClient.invalidateQueries({ queryKey: ['queue'] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const dirty =
    !save.isSuccess &&
    (pain !== '' ||
      site !== '' ||
      pregnancy !== '' ||
      Object.values(fall).some(Boolean) ||
      nutrition.weightLoss ||
      nutrition.reducedIntake);

  return (
    <FormScreen
      title={tr('opd', 'nursing_screening')}
      description={`${entry.patientName} · ${entry.mrn}`}
      icon={ClipboardList}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        setError(null);
        save.mutate();
      }}
    >
      <FormSection title={tr('opd', 'pain')} icon={Activity}>
        <Field label={tr('opd', 'pain_score_0_10')}>
          <Input type="number" min={0} max={10} value={pain} onChange={(e) => setPain(e.target.value)} />
        </Field>
        <Field label={tr('opd', 'where')}>
          <Input value={site} onChange={(e) => setSite(e.target.value)} />
        </Field>
      </FormSection>

      <FormSection title={tr('opd', 'fall_risk')} icon={Activity} columns={1}>
        <fieldset className="space-y-1">
          <legend className="sr-only">{tr('opd', 'fall_risk')}</legend>
          {FALL_ITEMS.map(([key, label]) => (
            <Checkbox key={key} checked={fall[key]} onCheckedChange={(checked) => setFall({ ...fall, [key]: checked })} label={label} />
          ))}
        </fieldset>
      </FormSection>

      <FormSection title={tr('opd', 'nutrition')} icon={Utensils} columns={1}>
        <fieldset className="space-y-1">
          <legend className="sr-only">{tr('opd', 'nutrition')}</legend>
          <Checkbox
            checked={nutrition.weightLoss}
            onCheckedChange={(checked) => setNutrition({ ...nutrition, weightLoss: checked })}
            label={tr('opd', 'lost_weight_without_trying')}
          />
          <Checkbox
            checked={nutrition.reducedIntake}
            onCheckedChange={(checked) => setNutrition({ ...nutrition, reducedIntake: checked })}
            label={tr('opd', 'eating_less_than_usual')}
          />
        </fieldset>
      </FormSection>

      {woman && (
        <FormSection title={tr('opd', 'pregnancy')} icon={Baby}>
          <Field label={tr('opd', 'could_she_be_pregnant')}>
            <ChoiceButtons
              layout="chips"
              value={pregnancy}
              onChange={setPregnancy}
              options={[
                { value: '', label: tr('opd', 'not_asked_2') },
                { value: 'not_pregnant', label: tr('opd', 'no') },
                { value: 'possible', label: tr('opd', 'possibly') },
                { value: 'pregnant', label: tr('opd', 'yes') },
                { value: 'unknown', label: tr('opd', 'does_not_know') },
              ]}
            />
          </Field>
        </FormSection>
      )}

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions submitLabel={tr('opd', 'save')} loading={save.isPending} cancelTo={returnTo} />
    </FormScreen>
  );
}
